import { Link, useLocation } from 'react-router-dom';
import {
    Card,
    CardBody,
    CardHeader,
    Row,
    Col,
    Alert,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faLightbulb,
    faInfoCircle,
    faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { AppNavPaths } from '../Common';
import './SupportToolLayout.scss';

export const SupportToolLayout = ({
    title,
    description,
    children,
    examples,
    warnings = [],
    tips = [],
}) => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location?.search ?? '');
    const isAdminRoute = location?.pathname?.startsWith(AppNavPaths.AdminTool);
    const origin = searchParams.get('origin') === 'admin' || isAdminRoute ? 'admin' : 'support';
    const backPath = origin === 'admin' ? AppNavPaths.AdminTool : AppNavPaths.SupportTools;
    const backLabel = origin === 'admin' ? 'Back to Admin Tools' : 'Back to Support Tools';

    return (
        <div className="support-tool-layout p-4 bg-light w-100 overflow-auto">
            <div className="mb-4">
                <Link
                    to={backPath}
                    className="btn btn-link text-muted p-0 mb-2 d-flex align-items-center"
                >
                    <FontAwesomeIcon
                        icon={faArrowLeft}
                        className="mr-2"
                    />
                    {backLabel}
                </Link>
                <h2 className="mb-1">{title}</h2>
                <p className="text-muted">{description}</p>
            </div>

            <Row>
                <Col
                    lg={7}
                    className="mb-4"
                >
                    <Card className="shadow-sm">
                        <CardHeader className="bg-white">
                            <h5 className="mb-0">Execute Tool</h5>
                        </CardHeader>
                        <CardBody>{children}</CardBody>
                    </Card>
                </Col>

                <Col
                    lg={5}
                    className="mb-4"
                >
                    {warnings.length > 0 && (
                        <Alert
                            color="warning"
                            className="mb-3"
                        >
                            <div className="d-flex align-items-start">
                                <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    className="mr-2 mt-1"
                                />
                                <div>
                                    <strong>Important:</strong>
                                    <ul className="mb-0 mt-2 pl-3">
                                        {warnings.map((warning, idx) => (
                                            <li key={idx}>{warning}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </Alert>
                    )}

                    {tips.length > 0 && (
                        <Alert
                            color="info"
                            className="mb-3"
                        >
                            <div className="d-flex align-items-start">
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    className="mr-2 mt-1"
                                />
                                <div>
                                    <strong>Tips:</strong>
                                    <ul className="mb-0 mt-2 pl-3">
                                        {tips.map((tip, idx) => (
                                            <li key={idx}>{tip}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </Alert>
                    )}

                    <Card className="shadow-sm">
                        <CardHeader className="bg-white">
                            <h5 className="mb-0 d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={faLightbulb}
                                    className="mr-2 text-warning"
                                />
                                Examples
                            </h5>
                        </CardHeader>
                        <CardBody className="examples-list">
                            {examples.map((example, idx) => (
                                <div
                                    key={idx}
                                    className={`example-item ${idx < examples.length - 1 ? 'mb-4' : ''}`}
                                >
                                    <h6 className="example-title font-weight-bold mb-1">{example.title}</h6>
                                    <p className="example-scenario text-muted mb-2">
                                        <strong>Scenario:</strong>
                                        {' '}
                                        {example.scenario}
                                    </p>
                                    <div className="example-steps">
                                        <strong>Steps:</strong>
                                        <ol className="mb-0 mt-1">
                                            {example.steps.map((step, stepIdx) => (
                                                <li key={stepIdx}>{step}</li>
                                            ))}
                                        </ol>
                                    </div>
                                </div>
                            ))}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};
